/**
 * MetricLadder — Escalera de rendimiento compacta (Top N en una línea por fila).
 *
 * QUÉ HACE: pinta 2–3 filas de una sola línea (`#. nombre  barra`); la barra va
 *   proporcional al mayor valor, con relleno esmeralda, carril con relieve y el
 *   **% dentro de la barra**. El nombre se trunca para no alterar el alto de la
 *   fila y el monto exacto queda en el título nativo. Las filas se reparten el
 *   alto libre.
 * QUÉ INVOCA: MetricProgressBar (UI compartida) y `useReducedMotion`.
 * QUÉ EXPORTA: MetricLadder, MetricLadderItem.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import type { CSSProperties } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerDelay } from "@/lib/animation/motion";
import { MetricProgressBar } from "./MetricProgressBar";

/** Fila de la escalera. */
export interface MetricLadderItem {
  /** Nombre del producto (se trunca si no cabe en una línea). */
  label: string;
  /** Valor crudo: define el ancho de la barra relativo al mayor. */
  value: number;
  /** Porcentaje normalizado ("42%"). */
  percentLabel: string;
  /** Monto formateado ("$30.7M"); se muestra en el título nativo. */
  valueLabel: string;
}

interface MetricLadderProps {
  items: MetricLadderItem[];
  /** Clases del relleno de la barra (color o degradado). */
  fillClassName?: string;
  className?: string;
}

export const MetricLadder = ({
  items,
  fillClassName = "bg-emerald-500",
  className = "",
}: MetricLadderProps) => {
  const reducedMotion = useReducedMotion();
  const max = items.reduce((top, item) => Math.max(top, item.value), 0);

  return (
    <div
      className={`flex min-h-0 flex-col gap-1.5 overflow-hidden ${className}`}
    >
      {items.map((item, index) => {
        const percent = max > 0 ? (item.value / max) * 100 : 0;
        return (
          <div
            key={`${item.label}-${index}`}
            title={`${item.label} · ${item.valueLabel}`}
            className={`flex min-w-0 items-center gap-2${
              reducedMotion ? "" : " animate-chart-fade"
            }`}
            style={
              reducedMotion
                ? undefined
                : ({ animationDelay: staggerDelay(index) } as CSSProperties)
            }
          >
            <span className="w-3 shrink-0 text-right text-[8px] font-semibold tabular-nums text-gray-400 dark:text-slate-500">
              {index + 1}.
            </span>
            <span className="min-w-0 max-w-[48%] shrink-0 truncate text-[8px] font-medium text-gray-700 dark:text-gray-200 sm:text-[9px]">
              {item.label}
            </span>
            <MetricProgressBar
              value={percent}
              fillClassName={fillClassName}
              heightClassName="h-2.5"
              label={item.percentLabel}
              className="min-w-0 flex-1"
            />
          </div>
        );
      })}
    </div>
  );
};
